<script setup lang="ts">
import { triggerDownloadFromBlob, triggerPrintFromBlob } from '@@/bits/blob'
import device from '@@/bits/device'
import type { ImagesToPdfPageData, ImagesToZipPageData } from '@@/bits/export_proxy'
import {
  buildImageExportProxyUrl,
  buildImagesToZipExportProxyUrl,
  fetchExportReadToken,
  fetchImagesToZipBlob,
} from '@@/bits/export_proxy'
import { __ } from '@@/bits/intl'
import { transformUrl } from '@@/bits/location'
import {
  SLIDESHOW_ANALYTICS_ID,
  SLIDESHOW_DEFAULT_EXPORT_QUERY_PARAMS,
  SLIDESHOW_SLIDE_DEFAULT_DELAY_WITHOUT_ATTACHMENT,
  SLIDESHOW_SLIDE_DEFAULT_DELAY_WITH_ATTACHMENT,
  buildSlideUrlForExport,
  buildSlideshowLink,
  getSlideshowExportFileName,
} from '@@/bits/slideshow'
import { SnackbarNotificationType } from '@@/enums'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, {
  OzMenuRowsFocusStyle,
  OzMenuRowsRowStyle,
  OzMenuRowsSizePreset,
} from '@@/library/v4/components/OzMenuRows.vue'
import OzScreenSizeAwareSideModal from '@@/library/v4/components/OzScreenSizeAwareSideModal.vue'
import { shareSlideshow } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { PageType, type Page } from '@@/types/slideshow'
import SurfacePanelHeader from '@@/vuecomponents/SurfacePanelHeader.vue'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import { useSlideshowAttachments } from '@@/vuecomposables/slideshow_attachments'
import { useSlideshowExports } from '@@/vuecomposables/slideshow_export'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useSlideshowPanels } from '@@/vuecomposables/slideshow_panels'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { CORS, makeElvisProxyUrl } from '@padlet/elvis-client'
import { fetchBlob } from '@padlet/fetch'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const globalSnackbarStore = useGlobalSnackbarStore()
const { publicKey, title, links } = storeToRefs(useSurfaceStore())
const { isSmallerThanTabletPortrait, isSmallerThanDesktop } = storeToRefs(useWindowSizeStore())
const { isSharePanelOpen, isShareViaAnotherAppPanelOpen, isEmbedPanelOpen } = useSlideshowPanels()
const { hasAttachmentsOnPage } = useSlideshowAttachments()
const { isFirstPage, pages, currentPageNumber, getPageLastUpdated, currentPage, currentPageUrl, coverPageUrl } =
  useSlideshowPages()
const { hasDarkBackgroundLuminance } = useSlideshowStyles()
const { copyToClipboard } = useCopyToClipboard()

const { canIAccessContentDirectly, amICollaborator } = storeToRefs(useSurfacePermissionsStore())
const { isViewingTranslatedPadlet } = storeToRefs(useSurfaceTranslationsStore())

// Check if user is a collaborator with restricted access (content protection = "Disable all")
const isCollaboratorWithRestrictedAccess = computed(() => {
  return amICollaborator.value && !canIAccessContentDirectly.value
})

const closePanel = () => {
  isSharePanelOpen.value = false
}

const copyUrlToClipboard = (url: string) =>
  copyToClipboard({
    text: isViewingTranslatedPadlet.value ? transformUrl(url, { search: {} }) : url,
    globalSnackbarOptions: {
      message: __('Link copied to clipboard'),
      notificationType: SnackbarNotificationType.success,
      timeout: 1500,
    },
  })

enum ActionType {
  CopySlideshowLink = 'CopySlideshowLink',
  CopySlideLink = 'CopySlideLink',
  Embed = 'Embed',
  Share = 'Share',
  SaveCurrentSlideAsImage = 'SaveCurrentSlideAsImage',
  SaveAllSlidesAsImages = 'SaveAllSlidesAsImages',
  SaveAsPdf = 'SaveAsPdf',
  Print = 'Print',
}

interface Group {
  name: string
  rows: OzMenuRowsRow<ActionType>[]
}

const actionListGroups = computed((): Group[] => {
  const shareRows: OzMenuRowsRow<ActionType>[] = [
    {
      icon: 'link',
      text: __('Copy link to slideshow'),
      eventPayload: ActionType.CopySlideshowLink,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-copy-slideshow-link-button'],
    },
    {
      icon: 'code',
      text: __('Embed in your blog or website'),
      eventPayload: ActionType.Embed,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-embed-button'],
      rightIcon: 'chevron_right_center',
      rightIconSize: 14,
      hoverText: isCollaboratorWithRestrictedAccess.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: isCollaboratorWithRestrictedAccess.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: isCollaboratorWithRestrictedAccess.value,
    },
    {
      icon: 'share_outline',
      text: __('Share via another app'),
      eventPayload: ActionType.Share,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-share-via-another-app-button'],
      rightIcon: 'chevron_right_center',
      rightIconSize: 14,
      hoverText: isCollaboratorWithRestrictedAccess.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: isCollaboratorWithRestrictedAccess.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: isCollaboratorWithRestrictedAccess.value,
    },
  ]

  if (!isFirstPage.value) {
    shareRows.splice(1, 0, {
      icon: 'link',
      text: __('Copy link to this slide'),
      eventPayload: ActionType.CopySlideLink,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-copy-slide-link-button'],
    })
  }

  const exportRows: OzMenuRowsRow<ActionType>[] = [
    {
      icon: 'picture_outline',
      text: __('Save current slide as image'),
      eventPayload: ActionType.SaveCurrentSlideAsImage,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-save-current-slide-as-image-button'],
      hoverText: !canIAccessContentDirectly.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: !canIAccessContentDirectly.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: !canIAccessContentDirectly.value,
    },
    {
      icon: 'photo_video_outline',
      text: __('Save every slide as image'),
      eventPayload: ActionType.SaveAllSlidesAsImages,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-save-every-slide-as-image-button'],
      hoverText: !canIAccessContentDirectly.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: !canIAccessContentDirectly.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: !canIAccessContentDirectly.value,
    },
    {
      icon: 'document_pdf',
      text: __('Save as PDF'),
      eventPayload: ActionType.SaveAsPdf,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-save-as-pdf-button'],
      hoverText: !canIAccessContentDirectly.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: !canIAccessContentDirectly.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: !canIAccessContentDirectly.value,
    },
    {
      icon: 'printer',
      text: __('Print'),
      eventPayload: ActionType.Print,
      analyticsId: SLIDESHOW_ANALYTICS_ID['slideshow-print-button'],
      hoverText: !canIAccessContentDirectly.value ? __('Disabled due to content protection settings') : undefined,
      rowStyle: !canIAccessContentDirectly.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Default,
      isDisabled: !canIAccessContentDirectly.value,
    },
  ]

  const shareGroup = {
    name: __('Share'),
    rows: shareRows,
  }
  const exportGroup = {
    name: __('Export'),
    rows: exportRows,
  }

  if (device.app) {
    return [shareGroup]
  }

  return [shareGroup, exportGroup]
})

const shareViaAnotherApp = () => {
  if (device.app) {
    postMessage(shareSlideshow())
  } else {
    isShareViaAnotherAppPanelOpen.value = true
  }
}

/*
 * EXPORTS
 */
const currentSnackbarUid = ref<string>('')
const makeExportRequest = async (
  exportCallback: (
    wallReadTokenAbortController: AbortController,
    exportProxyAbortController: AbortController,
  ) => Promise<void>,
  loadingMessage: string,
  errorMessage: string,
) => {
  const wallReadTokenAbortController = new AbortController()
  const exportProxyAbortController = new AbortController()

  const clearLoadingSnackbar = () => {
    globalSnackbarStore.removeSnackbar(loadingSnackbarUid)
  }

  const loadingSnackbarUid = globalSnackbarStore.setSnackbar({
    persist: true,
    message: loadingMessage,
    actionText: __('Cancel'),
    actionTextActions: [
      () => {
        wallReadTokenAbortController.abort()
        exportProxyAbortController.abort()
        clearLoadingSnackbar()
      },
    ],
  })

  currentSnackbarUid.value = loadingSnackbarUid

  try {
    // Intentional return await so promise rejection can be caught
    return await exportCallback(wallReadTokenAbortController, exportProxyAbortController)
  } catch (err) {
    if (wallReadTokenAbortController.signal.aborted || exportProxyAbortController.signal.aborted) {
      // User cancelled manually, do nothing
      return
    }
    globalSnackbarStore.setSnackbar({
      message: errorMessage,
      notificationType: SnackbarNotificationType.error,
    })
    throw err
  } finally {
    clearLoadingSnackbar()
  }
}

const getExportDelayForPage = (page: Page) =>
  hasAttachmentsOnPage(page) || page.pageType === PageType.Cover
    ? SLIDESHOW_SLIDE_DEFAULT_DELAY_WITH_ATTACHMENT
    : SLIDESHOW_SLIDE_DEFAULT_DELAY_WITHOUT_ATTACHMENT

const downloadCurrentSlideAsImage = async () => {
  makeExportRequest(
    async (wallReadTokenAbortController, exportProxyAbortController) => {
      // Capture values at the start because user can still navigate before the API calls resolve, which would update the current page values before they're used
      const pageToExport = {
        url: currentPageUrl.value,
        lastUpdated: getPageLastUpdated(currentPage.value),
        filename: `${getSlideshowExportFileName(title.value, `slide${currentPageNumber.value}`)}.jpeg`,
      }

      const readToken = await fetchExportReadToken(publicKey.value, {
        apiVersion: 1,
        signal: wallReadTokenAbortController.signal,
      })

      const slideUrlForExport = buildSlideUrlForExport(pageToExport.url, readToken, pageToExport.lastUpdated)
      const slideExportProxyUrl = buildImageExportProxyUrl({
        ...SLIDESHOW_DEFAULT_EXPORT_QUERY_PARAMS,
        url: slideUrlForExport,
        delay: getExportDelayForPage(currentPage.value!),
      })

      const exportProxyBlob = await fetchBlob(slideExportProxyUrl, {
        signal: exportProxyAbortController.signal,
        cors: true,
      })

      // We don't trigger a download by redirecting with window.location.href because there is no way to cancel the request
      // and the bulk of the time is spent waiting for the image to be generated by the export proxy.
      triggerDownloadFromBlob(exportProxyBlob, pageToExport.filename)
    },
    __('Generating slide image...'),
    __('Error generating slide image'),
  )
}

type PageData<T extends boolean> = T extends true ? ImagesToZipPageData : ImagesToPdfPageData
const buildPageDataForAllSlides = <T extends boolean>(
  readToken: string,
  { generateFileName }: { generateFileName: T },
): PageData<T>[] => {
  return pages.value.map((page, index) => {
    const slideshowLink = buildSlideshowLink(links.value.show, page.pageType, page.entityId)
    const pageLastUpdated = getPageLastUpdated(page)
    const slideUrlForExport = buildSlideUrlForExport(slideshowLink, readToken, pageLastUpdated)

    return {
      url: slideUrlForExport,
      file_name: generateFileName ? `slide${index + 1}` : undefined,
      delay: getExportDelayForPage(page),
    } as PageData<T>
  })
}

const downloadAllSlidesAsImages = async () => {
  makeExportRequest(
    async (wallReadTokenAbortController, exportProxyAbortController) => {
      const readToken = await fetchExportReadToken(publicKey.value, {
        apiVersion: 1,
        signal: wallReadTokenAbortController.signal,
      })

      const pageData = buildPageDataForAllSlides(readToken, { generateFileName: true })
      const fileName = getSlideshowExportFileName(title.value)
      const zipExportProxyUrl = buildImagesToZipExportProxyUrl({
        ...SLIDESHOW_DEFAULT_EXPORT_QUERY_PARAMS,
        filename: fileName,
      })
      const zipBlob = await fetchImagesToZipBlob(pageData, zipExportProxyUrl, {
        signal: exportProxyAbortController.signal,
      })

      const filename = `${fileName}.zip`
      triggerDownloadFromBlob(zipBlob, filename)
    },
    __('Generating slideshow images... this may take a few minutes'),
    __('Error generating slideshow images'),
  )
}

const downloadAllSlidesAsPdf = async (triggerMode: 'download' | 'print') => {
  makeExportRequest(
    async (_wallReadTokenAbortController, exportProxyAbortController) => {
      const { getSlideshowDocUrl } = useSlideshowExports({ abortSignal: exportProxyAbortController.signal })
      const url = await getSlideshowDocUrl()
      if (url == null) {
        globalSnackbarStore.removeSnackbar(currentSnackbarUid.value)
        globalSnackbarStore.setSnackbar({
          message: __('You will receive an email when the PDF is ready.'),
        })
        return
      }
      // Google storage cloud bucket public URLs are not CORS enabled, so we need to use the elvis proxy to make them work
      // We also set the expiry date to be 0 days so that we don't get cached files
      const elvisProxyUrl = makeElvisProxyUrl(url, { cors: CORS.ALL, expiryInDays: 0 })
      const blob = await fetchBlob(elvisProxyUrl, { cors: true })

      switch (triggerMode) {
        case 'download':
          triggerDownloadFromBlob(blob, getSlideshowExportFileName(title.value))
          break
        case 'print':
          triggerPrintFromBlob(blob)
          break
        default:
          break
      }
    },
    triggerMode === 'download' ? __('Generating slideshow PDF...') : __('Preparing slideshow for printing...'),
    triggerMode === 'download' ? __('Error generating slideshow PDF') : __('Error printing slideshow'),
  )
}

const handleAction = async (_event: unknown, actionType: ActionType) => {
  // Prevent disabled actions from being executed for collaborators with restricted access
  const isEmbedDisabled = isCollaboratorWithRestrictedAccess.value && actionType === ActionType.Embed
  const isShareDisabled = isCollaboratorWithRestrictedAccess.value && actionType === ActionType.Share
  
  if (isEmbedDisabled || isShareDisabled) {
    return
  }
  
  switch (actionType) {
    case ActionType.SaveCurrentSlideAsImage: {
      closePanel()
      await downloadCurrentSlideAsImage()
      break
    }
    case ActionType.SaveAllSlidesAsImages: {
      closePanel()
      await downloadAllSlidesAsImages()
      break
    }
    case ActionType.SaveAsPdf: {
      closePanel()
      await downloadAllSlidesAsPdf('download')
      break
    }
    case ActionType.Print: {
      closePanel()
      // Mobile devices can be problematic when calling print programmatically and result in a no-op
      // so we use the download method instead
      await downloadAllSlidesAsPdf(device.mobile ? 'download' : 'print')
      break
    }
    case ActionType.CopySlideshowLink:
      copyUrlToClipboard(coverPageUrl.value)
      break
    case ActionType.CopySlideLink:
      copyUrlToClipboard(currentPageUrl.value)
      break
    case ActionType.Share:
      shareViaAnotherApp()
      break
    case ActionType.Embed:
      isEmbedPanelOpen.value = true
      break
    default:
      break
  }
}
</script>

<template>
  <OzScreenSizeAwareSideModal
    class="font-sans"
    aria-labelledby="slideshow-share-panel-title"
    :full-size="true"
    :is-phone="isSmallerThanTabletPortrait"
    :width-class="isSmallerThanDesktop ? 'w-100' : 'w-120'"
    :dark-mode="hasDarkBackgroundLuminance"
    :z-index-class="'z-sidepanel'"
    :scrollbar-overlay="true"
    :top-inner-corner-radius="0"
    data-testid="slideshowSharePanel"
    @scrim-click="closePanel"
    @scrim-esc="closePanel"
  >
    <template #header>
      <SurfacePanelHeader class="pb-3">
        <template #start-content>
          <OzIconButton
            icon-name="close_small"
            data-testid="slideshowSharePanelCloseButton"
            :size-preset="OzIconButtonSizePreset.H32px"
            :label="__('Close slideshow share panel')"
            :color-scheme="OzIconButtonColorScheme.SecondaryClear"
            :dark-mode="hasDarkBackgroundLuminance"
            @click="closePanel"
          />
        </template>
        <template #center-content>
          <h2
            id="slideshow-share-panel-title"
            :class="[
              'text-heading-3 text-center font-semibold',
              hasDarkBackgroundLuminance ? 'text-light-text-100' : 'text-dark-text-100',
            ]"
          >
            {{ __('Share') }}
          </h2>
        </template>
      </SurfacePanelHeader>
    </template>
    <template #body>
      <div :class="'px-4 pt-3 pb-5 flex flex-col gap-y-5'">
        <div v-for="(group, i) in actionListGroups" :key="group.name">
          <p
            :class="[
              'text-body-small px-2 pb-2',
              hasDarkBackgroundLuminance ? 'text-light-text-200' : 'text-dark-text-200',
            ]"
          >
            {{ group.name }}
          </p>
          <OzBox :key="i" :radius="20" :color="OzBoxColors.Secondary" :dark-mode="hasDarkBackgroundLuminance">
            <OzMenuRows
              :accessibility-role="'group'"
              :size-preset="OzMenuRowsSizePreset.H48px"
              :focus-style="OzMenuRowsFocusStyle.Bubble100"
              :dark-mode="hasDarkBackgroundLuminance"
              :common-settings="{
                textTruncated: true,
              }"
              :rows="group.rows"
              @select-row="handleAction"
            />
          </OzBox>
        </div>
      </div>
    </template>
  </OzScreenSizeAwareSideModal>
</template>
